import { ConversationContainer } from '@modules/conversation/ConversationContainer';
import { MessageListContainer } from '@modules/conversation/MessageListContainer';
import { useConversationEditor } from '@modules/conversation/hooks/useConversationEditor';
import { useConversationDraft } from '@modules/conversation/state/ConversationDraft';
import { alpha, styled } from '@mui/material';
import cn from 'classnames';
import { useEffect } from 'react';
import { IChatConversation } from 'src/@types/chat';
import { useAuthContext } from 'src/auth/useAuthContext';
import { PageContainer } from 'src/components/PageContainer';
import { useGlobalAlert } from 'src/layouts/DashboardLayout/GlobalAlert/useGlobalAlert';
import { useChatContext } from 'src/provider/ChatContext';
import { ConversationContextProvider } from 'src/provider/ConversationContext';
import { markConversationAsRead } from 'src/redux/slices/chat';
import { useDispatch } from 'src/redux/store';
import { ChatConversationMessageInput } from 'src/sections/@dashboard/chat/ChatConversation/ChatConversationMessageInput';
import { ChatSearch } from 'src/sections/@dashboard/chat/ChatConversation/ChatSearch/ChatSearch';
import {
  ChatConversationContextProvider,
  useChatConversationContext,
} from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';
import { useSocketEvent } from 'src/socket';
import { onChatNewMessage } from 'src/socket/listeners/chat';
import { ChatHeader } from '../ChatHeader';
import { ChatMessageList } from './ChatMessageList';

type Props = {
  conversation: IChatConversation;
  isModal?: boolean;
  onClose?: () => void;
};

const ChatConversationInner = ({ conversation, isModal, onClose }: Props) => {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const { hasAlert } = useGlobalAlert();
  const { goToMessage, setGoToMessage: chatGoToMessage } = useChatContext();
  const { handleFileSelect, searchTerm, setGoToMessage, editorConfig } =
    useChatConversationContext();

  const { setContent } = useConversationDraft(conversation._id);
  const { editor } = useConversationEditor({
    conversationId: conversation._id,
    editorConfig,
  });

  const onFileSelect = (type: string, file: File) => {
    setContent(conversation._id, editor.getHTML());
    handleFileSelect(type, file);
  };

  useEffect(() => {
    if (!goToMessage) return;
    setGoToMessage(goToMessage);
    chatGoToMessage(null);
  }, [goToMessage, setGoToMessage, chatGoToMessage]);

  useSocketEvent(
    onChatNewMessage(async (message) => {
      if (
        message.author === user?._id ||
        message.channel !== conversation._id
      ) {
        return;
      }
      await dispatch(
        markConversationAsRead({
          conversationId: message.channel,
          messages: [message._id],
        }),
      );
    }),
  );

  return (
    <PageContainer className={cn({ fullHeight: isModal, hasAlert })}>
      <ChatHeader conversation={conversation} onClose={onClose} />
      <ConversationContainer
        handleFileSelect={onFileSelect}
        searchTerm={searchTerm}
        SearchComponent={ChatSearch}
      >
        <>
          <MessageListContainer>
            <ChatMessageList conversation={conversation} editor={editor} />
          </MessageListContainer>
          <StyledConversationInputContainer>
            <ChatConversationMessageInput
              editor={editor}
              onFileSelect={onFileSelect}
            />
          </StyledConversationInputContainer>
        </>
      </ConversationContainer>
    </PageContainer>
  );
};

export const ChatConversation = ({
  conversation,
  isModal = false,
  onClose,
}: Props) => (
  <ConversationContextProvider>
    <ChatConversationContextProvider conversation={conversation}>
      <ChatConversationInner
        conversation={conversation}
        isModal={isModal}
        onClose={onClose}
      />
    </ChatConversationContextProvider>
  </ConversationContextProvider>
);

const StyledConversationInputContainer = styled('div')(({ theme }) => {
  const color =
    theme.themeName === 'light'
      ? theme.palette.common.white
      : theme.palette.grey[900];
  return {
    background: `linear-gradient(180deg, ${alpha(color, 0)} 5%, ${alpha(color, 1)} 12%)`,
    marginTop: theme.spacing(-1.5),
    zIndex: 1,
  };
});
